import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import classnames from "classnames"
import { Button, Form, Container, Row, Col } from 'reactstrap'
import _ from "lodash"
import { useFormContext } from 'react-hook-form'
import { useSelector } from 'react-redux'

import { trans } from '@utils'
import { ButtonSpinner } from '@src/components'
import { CanCall } from "@modules/user"

import FieldsGroup from "./FieldsGroup"
import { CustomFieldTypes } from "../../assets/data/constants"

const EntityDetails = props => {
  //************************************//
  const {customFieldsState, entityName, emptyDndDroppableText, staticFields = [], showActions = true, children} = props
  const {customFields, setCustomFields} = customFieldsState
  //************************************//
  const [entityCustomDetail, setEntityCustomDetail] = useState({})
  const [lastSavedState, setLastSavedState] = useState()
  const [isItemsChanged, setIsItemsChanged] = useState(false)
  //************************************//
  const loading = useSelector(store => store.app.loading)
  //************************************//
  const form = useFormContext()
  //************************************//
  const { formState: { isDirty }, watch, getValues, trigger, reset } = form
  //************************************//
  const onSubmit = async () => {
    const data = getValues()
    const checkErr = await trigger()
    if (!checkErr) {
      return
    }
    const finalData = []
    const currentItemsArr = _.cloneDeep(_.filter(customFields.entityCustomFieldsList, x => !_.isNull(x.parent_id)))
    _.forEach(currentItemsArr, item => {
      if (!_.isEqual(item, _.find(lastSavedState, x => x.key === item.key))) {
        finalData.push({ ...item, is_used: true, parent_id: null })
      }
    })
    const unusedItems = _.filter(lastSavedState, x => !_.find(customFields.entityCustomFieldsList, finalItem => finalItem.key === x.key) && !_.isNull(x.parent_id))
    const order = {}
    _.forEach(currentItemsArr, (item, index) => {
      order[item.key] = index
    })
    props.onSubmit(
      {
        ...data,
        fields: {
          data: [...finalData, ..._.map(unusedItems, x => ({ ...x, is_used: false, parent_id: null }))],
          order
        }
      },
      (serverData) => {
        const data = {}
        _.forEach(getValues(), (x, key) => {
          data[key] = serverData[key]
        })
        reset(data, { keepValues: false, keepDirty: false, keepDefaultValues: false })
        const listArr = [
          _.find(customFields.entityCustomFieldsList, x => _.isNull(x.parent_id)),
          ..._.map(serverData.fields, x => ({ ...x, parent_id: "group1" }))
        ]
        setCustomFields(prevState => {
          return {
            ...prevState,
            entityCustomFieldsList: listArr,
            refresh: !prevState.refresh
          }
        })
        setLastSavedState(_.cloneDeep(listArr))
      }
    )
  }
  //************************************//
  const checkChangedState = (lastSaved) => {
    const tempLastSaved = lastSaved ?? lastSavedState
    let equalsCond = false
    const currentOrderArr = _.map(customFields.entityCustomFieldsList, x => x.key)
    const lastSavedOrderArr = _.map(tempLastSaved, x => x.key)
    if (!_.isEqual(currentOrderArr, lastSavedOrderArr)) {
      equalsCond = true
    }
    if (!_.isEqual(customFields.entityCustomFieldsList, tempLastSaved)) {
      equalsCond = true
    }
    return equalsCond
  }
  //************************************//
  useEffect(() => {
    setIsItemsChanged(checkChangedState() || isDirty)
  }, [customFields.entityCustomFieldsList, customFields.refresh, lastSavedState, isDirty])
  //************************************//
  useEffect(() => {
    setLastSavedState(_.cloneDeep(customFields.entityCustomFieldsList))
    setEntityCustomDetail(getValues())
  }, [])
  //************************************//
  const resetValues = () => {
    setCustomFields(prevState => {
      reset(entityCustomDetail, { keepValues: false, keepDirty: false, keepDefaultValues: false })
      return {
        ...prevState,
        entityCustomFieldsList: lastSavedState,
        refresh: prevState.refresh
      }
    })
  }
  //************************************//
  return (
    <div className={'d-flex flex-column'}>
      <div className={'w-100'}>
        {children}
        {
          _.size(staticFields) > 0 && (
            <div className={'mt-1'}>
              <div
                className={classnames(`entity-custom-columns border rounded p-1`)}
              >
                {
                  _.map(staticFields, (staticField, index) => {
                    return (
                      <div key={index} className={'flex-1'}>
                        <div className={classnames('entity-custom-field-draggable-row bg-white my-50')}>
                          <div
                            className={classnames('entity-custom-field-item-row d-flex p-50 align-items-center position-relative', {
                              "border-left-3 border-left-danger": staticField.required,
                              "border-left-3 border-left-white": !staticField.required
                            })}
                          >
                            <div className={'d-flex w-50 align-items-center'}>
                              <span className={classnames('ml-1')}>
                                {_.get(staticField, 'name')}
                              </span>
                            </div>
                            <div className={'w-50'}>
                              <span className={classnames('text-muted px-1')}>
                                {trans(_.get(CustomFieldTypes, `${staticField?.field_type}.label`, ''))}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })
                }
              </div>
            </div>
          )
        }
      </div>
      <div className={'w-100'}>
        {
          _.map(_.filter(customFields.entityCustomFieldsList, x => _.isNull(x.parent_id)), (group, groupIndex) => {
            return (
              <FieldsGroup
                key={group.key}
                entityName={entityName}
                groupIndex={groupIndex}
                customFieldsState={customFieldsState}
                group={group}
                groupChildren={_.filter(customFields.entityCustomFieldsList, item => item.parent_id === group.key)}
                emptyDndDroppableText={emptyDndDroppableText}
              />
            )
          })
        }
      </div>

      {_.isNil(customFields?.entityData?.deleted_at) && (
        <div className={'d-flex w-100'}>
          <div className={'w-100 d-flex justify-content-end mt-1'}>
            <Button color={'flat-secondary'} className={'mx-25'} disabled={!isItemsChanged || loading}
              onClick={resetValues}>
              <span>{trans('customFields.discard')}</span>
            </Button>

            <CanCall action={props.submitAction}>
              <Button color={isItemsChanged ? 'primary' : 'flat-secondary'} disabled={!isItemsChanged || loading}
                onClick={onSubmit}>
                {loading && <ButtonSpinner />}
                <span className={classnames({ 'text-muted': !isItemsChanged })}>{trans('customFields.saveChanges')}</span>
              </Button>
            </CanCall>
          </div>
        </div>
      )}
    </div>
  )
}

EntityDetails.propTypes = {}

export default EntityDetails